/**
 * The plan view's coordinate system: model millimetres <-> SVG user units.
 *
 * One affine map, both directions, in one place. Everything the view draws goes
 * through `x`/`y`/`len`, and everything a pointer produces comes back through
 * `toModel`. Keeping it pure (no DOM, no React) is what makes "the drawing is
 * to scale" an assertable property rather than a visual impression: a test can
 * ask where a 2400 mm wall lands and compare it against the room rectangle.
 *
 * Frame of reference matches the interior domain: X across the building, Z
 * along it, origin at the building centre, millimetres. Screen Y increases
 * downward and model Z increases toward the front of the building, which is
 * what a plan drawing wants — the entrance reads at the bottom of the page.
 */

/** An axis-aligned rectangle in model millimetres. */
export interface PlanBoundsMm {
	minXMm: number;
	maxXMm: number;
	minZMm: number;
	maxZMm: number;
}

/** Transient zoom/pan. Not design data — it never reaches the store. */
export interface PlanViewTransform {
	/** 1 = the whole drawing fits the frame. */
	zoom: number;
	/** Pan in model millimetres, applied before zoom. */
	panXMm: number;
	panZMm: number;
}

export const IDENTITY_PLAN_TRANSFORM: PlanViewTransform = {
	zoom: 1,
	panXMm: 0,
	panZMm: 0,
};

export interface PlanProjection {
	/** SVG user units per model millimetre. */
	readonly scale: number;
	/** `viewBox` attribute value, ready to spread onto the <svg>. */
	readonly viewBox: string;
	readonly viewBoxMinX: number;
	readonly viewBoxMinY: number;
	readonly viewBoxWidth: number;
	readonly viewBoxHeight: number;
	/** Model X (mm) -> SVG x. */
	x(xMm: number): number;
	/** Model Z (mm) -> SVG y. */
	y(zMm: number): number;
	/** A length in mm -> a length in SVG user units. */
	len(mm: number): number;
	/** SVG x -> model X (mm). */
	toModelX(svgX: number): number;
	/** SVG y -> model Z (mm). */
	toModelZ(svgY: number): number;
}

export interface CreatePlanProjectionOptions {
	/**
	 * Margin around the building, in model millimetres. The dimension chains
	 * live here, so it is expressed in the drawing's own units rather than in
	 * pixels — otherwise the chains would crowd the walls at large sauna sizes
	 * and float away at small ones.
	 */
	paddingMm?: number;
	/** SVG user units per millimetre at zoom 1. */
	unitsPerMm?: number;
	transform?: PlanViewTransform;
}

export const DEFAULT_PLAN_PADDING_MM = 700;

/**
 * Build the projection for a building envelope.
 *
 * `unitsPerMm` is deliberately fixed rather than fitted to a pixel viewport:
 * the SVG scales itself through `viewBox` + `preserveAspectRatio`, so the user
 * units stay proportional to millimetres at every screen size. That is the
 * whole reason the drawing survives being dropped into a PDF at a stated scale.
 */
export function createPlanProjection(
	bounds: PlanBoundsMm,
	options: CreatePlanProjectionOptions = {},
): PlanProjection {
	const paddingMm = options.paddingMm ?? DEFAULT_PLAN_PADDING_MM;
	const unitsPerMm = options.unitsPerMm ?? 0.1;
	const { zoom, panXMm, panZMm } = options.transform ?? IDENTITY_PLAN_TRANSFORM;
	const safeZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;

	const fullWidthMm = bounds.maxXMm - bounds.minXMm + paddingMm * 2;
	const fullHeightMm = bounds.maxZMm - bounds.minZMm + paddingMm * 2;

	const scale = unitsPerMm;
	// Zoom shrinks the visible WINDOW rather than growing the geometry, so the
	// mm -> user-unit scale is constant and `len()` stays honest at every zoom.
	const viewBoxWidth = (fullWidthMm * scale) / safeZoom;
	const viewBoxHeight = (fullHeightMm * scale) / safeZoom;

	const centreXMm = (bounds.minXMm + bounds.maxXMm) / 2 + panXMm;
	const centreZMm = (bounds.minZMm + bounds.maxZMm) / 2 + panZMm;

	const viewBoxMinX = centreXMm * scale - viewBoxWidth / 2;
	const viewBoxMinY = centreZMm * scale - viewBoxHeight / 2;

	return {
		scale,
		viewBox: `${viewBoxMinX} ${viewBoxMinY} ${viewBoxWidth} ${viewBoxHeight}`,
		viewBoxMinX,
		viewBoxMinY,
		viewBoxWidth,
		viewBoxHeight,
		x: (xMm) => xMm * scale,
		y: (zMm) => zMm * scale,
		len: (mm) => mm * scale,
		toModelX: (svgX) => svgX / scale,
		toModelZ: (svgY) => svgY / scale,
	};
}

/** A DOM rect, reduced to what the mapping needs (so tests need no DOM). */
export interface PlanClientRect {
	left: number;
	top: number;
	width: number;
	height: number;
}

/**
 * Client (viewport) pixels -> SVG user units, for
 * `preserveAspectRatio="xMidYMid meet"`.
 *
 * Done by hand rather than through `getScreenCTM()` for two reasons: jsdom does
 * not implement `getScreenCTM`, which would make every drag test unwritable;
 * and the letterboxing rule is three lines of arithmetic that are easier to
 * assert than to mock. "meet" fits the whole viewBox inside the element and
 * centres the leftover on both axes — so the smaller of the two ratios wins and
 * the surplus is split in half.
 */
export function clientToSvgPoint(
	clientX: number,
	clientY: number,
	rect: PlanClientRect,
	projection: PlanProjection,
): { svgX: number; svgY: number } {
	if (rect.width <= 0 || rect.height <= 0) {
		return { svgX: projection.viewBoxMinX, svgY: projection.viewBoxMinY };
	}

	const fit = Math.min(
		rect.width / projection.viewBoxWidth,
		rect.height / projection.viewBoxHeight,
	);
	const renderedWidth = projection.viewBoxWidth * fit;
	const renderedHeight = projection.viewBoxHeight * fit;
	const letterboxX = (rect.width - renderedWidth) / 2;
	const letterboxY = (rect.height - renderedHeight) / 2;

	return {
		svgX: (clientX - rect.left - letterboxX) / fit + projection.viewBoxMinX,
		svgY: (clientY - rect.top - letterboxY) / fit + projection.viewBoxMinY,
	};
}

/** Client pixels straight to model millimetres — the drag pipeline's entry. */
export function clientToModelMm(
	clientX: number,
	clientY: number,
	rect: PlanClientRect,
	projection: PlanProjection,
): { xMm: number; zMm: number } {
	const { svgX, svgY } = clientToSvgPoint(clientX, clientY, rect, projection);
	return {
		xMm: projection.toModelX(svgX),
		zMm: projection.toModelZ(svgY),
	};
}
